import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiMenuDown } from '@mdi/js';

import { Button } from 'common/components/Button';
import { Chip } from 'common/components/Chip';
import { DropdownMenu } from 'common/components/DropdownMenu';
import { Icon } from 'common/components/Icon';
import { formatDate } from 'common/utils/formatDate';
import { IOfferEventDetailFragment, IOfferEventType } from 'graphql/types';
import { useGroupConditionRows } from 'offer/hooks/useGroupConditionRows';
import { getIsEnrollmentNotYetOpen } from 'offer/utils/offerEvent';

import { EnrollParticipantsDrawer } from './EnrollParticipantsDrawer';
import { GroupConditionEnrollments } from './GroupConditionEnrollments';

interface IProps {
    canManageParticipants?: boolean;
    loading?: boolean;
    offerEvent: IOfferEventDetailFragment;
    onClickSubscribe(groupId?: string): void;
}

export const SubscribeSection = ({
    canManageParticipants,
    loading,
    offerEvent,
    onClickSubscribe,
}: IProps) => {
    const [translate] = useTranslation();
    const [isUserSelectorOpen, setIsUserSelectorOpen] = useState(false);

    const {
        spotsLeft,
        waitingList,
        maxSubscriptions,
        type,
        hasGroupConditionLimits,
        openingDate,
    } = offerEvent;

    const enrollmentNotYetOpen = getIsEnrollmentNotYetOpen(offerEvent);

    const spotsAmount = !!spotsLeft && spotsLeft > 0 ? spotsLeft : 0;

    const hasMaxSubscriptions = maxSubscriptions > 0;
    const showWaitingListText =
        waitingList && hasMaxSubscriptions && !spotsAmount;
    const showSubscriptionsFullText =
        !waitingList && !spotsAmount && hasMaxSubscriptions;
    const showSubscribeButton =
        !!spotsAmount || !hasMaxSubscriptions || waitingList;

    const isWebinarOrMeeting =
        type === IOfferEventType.Webinar || type === IOfferEventType.Meeting;

    const {
        rows: groupConditionRows,
        loading: groupConditionRowsLoading,
        canEnrollParticipantsAsGroupManager,
    } = useGroupConditionRows({
        canManageParticipants,
        offerEvent,
    });

    const hasEnrollableRows = groupConditionRows.some(
        (row) => row.canEnrollParticipants
    );

    // Manager-only rows must remain visible even when the event itself can no
    // longer be subscribed to (e.g. fully booked, no waiting list).
    const showGroupConditionSubscribeButtons =
        !groupConditionRowsLoading &&
        isWebinarOrMeeting &&
        hasGroupConditionLimits &&
        (showSubscribeButton || hasEnrollableRows);

    // While membership is loading we can't tell yet which variant to render,
    // so hide both to avoid flickering from the general button to the
    // per-group buttons once the query resolves.
    const showGeneralSubscribeStack =
        !groupConditionRowsLoading &&
        (!!showSubscribeButton ||
            !!spotsAmount ||
            showWaitingListText ||
            showSubscriptionsFullText) &&
        !showGroupConditionSubscribeButtons;

    // Reset the user-selector state when the drawer is no longer reachable so
    // it doesn't reappear pre-opened if the conditions flip back.
    const enrollDrawerAvailable =
        canEnrollParticipantsAsGroupManager &&
        !showGroupConditionSubscribeButtons;

    useEffect(() => {
        if (!enrollDrawerAvailable) {
            setIsUserSelectorOpen(false);
        }
    }, [enrollDrawerAvailable]);

    let fullWaitingListDisplay;

    if (showWaitingListText || showSubscriptionsFullText) {
        fullWaitingListDisplay = (
            <Box>
                <Typography variant="body2">
                    {translate(
                        showWaitingListText
                            ? 'offerEventForm.waitingListFull'
                            : 'offerEventForm.subscriptionsFull'
                    )}
                </Typography>
            </Box>
        );
    }

    // Enrollment hasn't opened yet: show only the "you can enroll from" message.
    // This wins over the subscribe button and the group condition options.
    if (enrollmentNotYetOpen) {
        return (
            <Box sx={{ mt: 1 }}>
                <Typography role="status" variant="body2">
                    {translate('offerEventForm.enrollFrom', {
                        date: formatDate(
                            openingDate,
                            'd MMMM yyyy HH:mm',
                            false,
                            true
                        ),
                    })}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {showGroupConditionSubscribeButtons && (
                <GroupConditionEnrollments
                    groupConditionRows={groupConditionRows}
                    loading={loading}
                    offerEvent={offerEvent}
                    showBottomDivider={
                        showWaitingListText || showSubscriptionsFullText
                    }
                    onClickSubscribe={onClickSubscribe}
                />
            )}

            {!showGeneralSubscribeStack && fullWaitingListDisplay}

            {showGeneralSubscribeStack && (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={3}
                        sx={{ alignItems: { sm: 'center' } }}
                    >
                        {showSubscribeButton &&
                            !showGroupConditionSubscribeButtons &&
                            (canEnrollParticipantsAsGroupManager ? (
                                <ButtonGroup
                                    disableElevation
                                    color="primary"
                                    sx={{
                                        flex: 'none',
                                        maxWidth: 'fit-content',
                                    }}
                                    variant="contained"
                                >
                                    <Button
                                        color="primary"
                                        loading={loading}
                                        variant="contained"
                                        onClick={() => onClickSubscribe()}
                                    >
                                        {showWaitingListText
                                            ? translate(
                                                  'offerEventForm.waitingListSubscribe'
                                              )
                                            : translate('register')}
                                    </Button>
                                    <DropdownMenu
                                        anchor={
                                            <Button
                                                aria-label={translate(
                                                    'actions'
                                                )}
                                                color="primary"
                                                sx={{ px: 1, py: 0 }}
                                                variant="contained"
                                            >
                                                <Icon
                                                    path={mdiMenuDown}
                                                    size="2.4rem"
                                                />
                                            </Button>
                                        }
                                        placement="bottom-end"
                                    >
                                        <MenuItem
                                            onClick={() =>
                                                setIsUserSelectorOpen(true)
                                            }
                                        >
                                            {translate(
                                                'offerEventDetail.enrollParticipants'
                                            )}
                                        </MenuItem>
                                    </DropdownMenu>
                                </ButtonGroup>
                            ) : (
                                <Button
                                    color="primary"
                                    loading={loading}
                                    sx={{
                                        flex: 'none',
                                        maxWidth: 'fit-content',
                                    }}
                                    variant="contained"
                                    onClick={() => onClickSubscribe()}
                                >
                                    {showWaitingListText
                                        ? translate(
                                              'offerEventForm.waitingListSubscribe'
                                          )
                                        : translate('register')}
                                </Button>
                            ))}

                        {!!spotsAmount && (
                            <Box sx={{ ml: 3 }}>
                                <Chip
                                    label={translate('spotsLeft', {
                                        count: spotsAmount,
                                    })}
                                />
                            </Box>
                        )}

                        {fullWaitingListDisplay}
                    </Stack>
                </Box>
            )}

            {enrollDrawerAvailable && (
                <EnrollParticipantsDrawer
                    offerEvent={offerEvent}
                    open={isUserSelectorOpen}
                    onOpenChange={setIsUserSelectorOpen}
                />
            )}
        </>
    );
};
